/**
 * BulkOperationsPage (epic-6 §7 / BULK-1..4) — list, builder (actions rows +
 * target events + timeout + approval + listen), and detail with live child
 * states (poll status) + outputs array viewer + cancel.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Play, Pause, Plus, Trash2, ListChecks, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { DataTable } from '@/components/shared/DataTable';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { JsonViewer } from '@/components/shared/JsonViewer';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useToast } from '@/components/shared/Toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useGetBulkOperationsQuery,
  useCreateBulkOperationMutation,
  useDeleteBulkOperationMutation,
  useRunBulkOperationMutation,
  useCancelBulkOperationMutation,
  useGetBulkOperationStatusQuery,
  useListenBulkOperationMutation,
} from '@/store/api/bulkApi';
import { useGetRegistryServicesQuery, useGetPoliciesByUserQuery } from '@/store/api/policyBuilderApi';
import { useGetProjectsQuery } from '@/store/api/projectsApi';
import { useAppSelector } from '@/store/hooks';
import { MultiSelect } from '@/components/agents/MultiSelect';
import { JsonField } from '@/components/agents/JsonField';
import { assertApprovalContext } from '@/pages/bulkApproval';
import type { BulkOperation, BulkAction, BulkTargetEvent, BulkOperationStatus } from '@/types';
import { format } from 'date-fns';

const STATUS_VARIANT: Record<BulkOperationStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  draft: 'secondary',
  'pending-approval': 'outline',
  running: 'default',
  completed: 'default',
  timeout: 'destructive',
  cancelled: 'destructive',
  failed: 'destructive',
};

export function BulkOperationsPage() {
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState<BulkOperation | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BulkOperation | null>(null);

  const { data: bulkOps, isLoading, refetch } = useGetBulkOperationsQuery();
  const [run] = useRunBulkOperationMutation();
  const [cancel] = useCancelBulkOperationMutation();
  const [deleteBulkOperation] = useDeleteBulkOperationMutation();

  const list = (bulkOps as BulkOperation[] | undefined) ?? [];

  const columns: ColumnDef<BulkOperation, unknown>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <ListChecks className="h-4 w-4 text-muted-foreground shrink-0" />
          <div>
            <p className="font-medium text-sm">{row.original.name}</p>
            <p className="text-xs text-muted-foreground">
              {row.original.actions.length} action(s){row.original.targetEvents.length > 0 ? ` · ${row.original.targetEvents.length} target event(s)` : ''}
            </p>
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <Badge variant={STATUS_VARIANT[row.original.status] ?? 'secondary'} className="text-xs">{row.original.status}</Badge>,
    },
    {
      accessorKey: 'requiresApproval',
      header: 'Approval',
      cell: ({ row }) => (row.original.requiresApproval ? <Badge variant="outline" className="text-xs">required</Badge> : <span className="text-xs text-muted-foreground">—</span>),
    },
    {
      accessorKey: 'timeoutMs',
      header: 'Timeout',
      cell: ({ row }) => <span className="font-mono text-xs">{Math.round(row.original.timeoutMs / 1000)}s</span>,
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => format(new Date(row.original.createdAt), 'MMM d, yyyy'),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const op = row.original;
        return (
          <div className="flex gap-1">
            {op.status === 'draft' && (
              <Button
                variant="ghost"
                size="icon"
                title="Run"
                onClick={() =>
                  run(op._id)
                    .unwrap()
                    .then((r) => toast.success((r as { approvalId?: string }).approvalId ? 'Approval created' : 'Started'))
                    .catch((err) => toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to run'))
                }
              >
                <Play className="h-4 w-4 text-green-600" />
              </Button>
            )}
            {(op.status === 'running' || op.status === 'pending-approval') && (
              <Button
                variant="ghost"
                size="icon"
                title="Cancel"
                onClick={() =>
                  cancel(op._id)
                    .unwrap()
                    .then(() => toast.success('Cancelled'))
                    .catch((err) => toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Cancel failed'))
                }
              >
                <Pause className="h-4 w-4 text-orange-600" />
              </Button>
            )}
            <Button variant="ghost" size="icon" title="Details" onClick={() => setSelected(op)}>
              <ListChecks className="h-4 w-4" />
            </Button>
            {op.status === 'draft' && (
              <Button variant="ghost" size="icon" title="Delete" onClick={() => setDeleteTarget(op)}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Bulk Operations</h1>
          <p className="text-sm text-muted-foreground">
            Orchestrate multi-service actions with target events (BULK-1..4)
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()}>Refresh</Button>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New Operation
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : list.length === 0 ? (
            <EmptyState title="No bulk operations" description="Create an operation to orchestrate actions across services" />
          ) : (
            <DataTable columns={columns} data={list} onRowClick={setSelected} />
          )}
        </CardContent>
      </Card>

      <CreateBulkDialog open={createOpen} onOpenChange={setCreateOpen} />

      {selected && <BulkDetailDialog op={selected} onClose={() => setSelected(null)} />}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Operation"
        description={`Delete draft "${deleteTarget?.name}"?`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={async () => {
          if (deleteTarget) {
            try {
              await deleteBulkOperation(deleteTarget._id).unwrap();
              setDeleteTarget(null);
            } catch (err) {
              toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to delete');
            }
          }
        }}
      />
    </div>
  );
}

// ─── Create / builder dialog ──────────────────────────────────────────────────

function CreateBulkDialog(props: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const toast = useToast();
  const [create, { isLoading }] = useCreateBulkOperationMutation();
  const { data: registryServices } = useGetRegistryServicesQuery();
  const currentUserId = useAppSelector((s) => s.auth.user?._id ?? (s.auth.user as any)?.id);
  const { data: projects } = useGetProjectsQuery();
  // The bulk runs under policies OWNED BY the current user (backend
  // validatePolicyOwnership) — same endpoint RolesPage uses for its policy picker.
  const { data: userPolicies, isFetching: policiesLoading } = useGetPoliciesByUserQuery(currentUserId ?? '', { skip: !currentUserId });

  const serviceList = (registryServices ?? []).map((s) => s.name);
  const methodList = ['get', 'post', 'put', 'patch', 'delete', 'execute', 'start', 'stop', 'sync', 'search'];
  const projectList = (projects ?? []) as Array<{ _id: string; name: string }>;
  const policyList = (userPolicies ?? []) as Array<{ _id: string; name: string; isActive?: boolean }>;

  const [form, setForm] = useState({
    name: '',
    description: '',
    timeoutMs: 600000,
    requiresApproval: false,
    listenerEnabled: false,
    projectId: '',
    policyIds: [] as string[],
  });
  const [actions, setActions] = useState<Array<{ service: string; method: string; payload: string }>>([]);
  const [targetEvents, setTargetEvents] = useState<Array<{ service: string; method: string; payloadTemplate: string }>>([]);

  const emptyAction = () => ({ service: serviceList[0] ?? 'storage', method: 'get', payload: '{}' });
  const emptyEvent = () => ({ service: serviceList[0] ?? 'storage', method: 'post', payloadTemplate: '{}' });

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (actions.length === 0) {
      toast.error('Add at least one action');
      return;
    }
    const approvalError = assertApprovalContext(form);
    if (approvalError) {
      toast.error(approvalError);
      return;
    }
    const parsed: BulkAction[] = [];
    for (const a of actions) {
      try {
        parsed.push({ service: a.service, method: a.method, payload: JSON.parse(a.payload || '{}') });
      } catch {
        toast.error(`Action payload for ${a.service}:${a.method} is not valid JSON`);
        return;
      }
    }
    const events: BulkTargetEvent[] = [];
    for (const e of targetEvents) {
      try {
        events.push({ service: e.service, method: e.method, payloadTemplate: JSON.parse(e.payloadTemplate || '{}') });
      } catch {
        toast.error(`Target event payload for ${e.service}:${e.method} is not valid JSON`);
        return;
      }
    }
    try {
      await create({
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        projectId: form.projectId || undefined,
        timeoutMs: form.timeoutMs,
        requiresApproval: form.requiresApproval,
        policyIds: form.policyIds,
        actions: parsed,
        targetEvents: events,
      }).unwrap();
      toast.success('Operation created (draft)');
      props.onOpenChange(false);
      setForm({ name: '', description: '', timeoutMs: 600000, requiresApproval: false, listenerEnabled: false, projectId: '', policyIds: [] });
      setActions([]);
      setTargetEvents([]);
    } catch (err) {
      toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to create operation');
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Bulk Operation</DialogTitle>
          <DialogDescription>
            Actions run in sequence; target events carry {'{{outputs}}'} templates (BULK-2).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Timeout (ms)</Label>
              <Input
                type="number"
                min={1000}
                step={1000}
                value={form.timeoutMs}
                onChange={(e) => setForm({ ...form, timeoutMs: Number(e.target.value) || 600000 })}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Description</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="reqApproval"
              checked={form.requiresApproval}
              onCheckedChange={(checked) => setForm({ ...form, requiresApproval: checked === true })}
            />
            <Label htmlFor="reqApproval" className="text-sm">Requires approval</Label>
          </div>

          {/* Project + policies — required by the approvals service when
              requiresApproval is checked (BULK-2). */}
          <div className="space-y-3 rounded-md border p-3">
            <div className="flex flex-col space-y-1">
              <Label className="text-xs">Project</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.projectId}
                onChange={(e) => setForm({ ...form, projectId: e.target.value })}
              >
                <option value="">{projectList.length === 0 ? 'No projects available' : '— select project —'}</option>
                {projectList.map((p) => (
                  <option key={p._id} value={p._id}>{p.name}</option>
                ))}
              </select>
              {form.requiresApproval && projectList.length === 0 && (
                <p className="text-xs text-muted-foreground">No projects available — create one first before enabling approval.</p>
              )}
            </div>
            <MultiSelect
              label="Policies (scope of the approval + the run)"
              options={policyList.map((p) => ({
                value: p._id,
                label: p.name,
                meta: p.isActive === false ? 'inactive' : undefined,
              }))}
              value={form.policyIds}
              onChange={(next) => setForm({ ...form, policyIds: next })}
              placeholder={policiesLoading ? 'Loading policies…' : 'No policies found for this user'}
            />
            {form.requiresApproval && (
              <p className="text-xs text-muted-foreground">
                Approval requires a project and at least one policy — the approvals service uses the
                policy as the agent base policy when minting the approval token.
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="space-y-2 rounded-md border p-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Actions</Label>
              <Button variant="outline" size="sm" onClick={() => setActions([...actions, emptyAction()])}>
                <Plus className="mr-1 h-3 w-3" /> Add action
              </Button>
            </div>
            {actions.length === 0 && (
              <p className="text-xs text-muted-foreground">No actions yet — add the first step.</p>
            )}
            {actions.map((a, i) => (
              <div key={i} className="space-y-2 rounded-md border p-2">
                <div className="flex gap-2">
                  <select
                    className="flex h-9 flex-1 rounded-md border border-input bg-background px-2 text-sm"
                    value={a.service}
                    onChange={(e) => setActions(actions.map((x, idx) => (idx === i ? { ...x, service: e.target.value } : x)))}
                  >
                    {serviceList.length > 0 ? serviceList.map((s) => <option key={s} value={s}>{s}</option>) : <option value="storage">storage</option>}
                  </select>
                  <select
                    className="flex h-9 w-32 rounded-md border border-input bg-background px-2 text-sm"
                    value={a.method}
                    onChange={(e) => setActions(actions.map((x, idx) => (idx === i ? { ...x, method: e.target.value } : x)))}
                  >
                    {methodList.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => setActions(actions.filter((_, idx) => idx !== i))}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
                <JsonField label="Payload" value={a.payload} onChange={(v) => setActions(actions.map((x, idx) => (idx === i ? { ...x, payload: v } : x)))} height={80} hint="JSON body of the invoke call." />
              </div>
            ))}
          </div>

          {/* Target events */}
          <div className="space-y-2 rounded-md border p-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Target events (emit after success)</Label>
              <Button variant="outline" size="sm" onClick={() => setTargetEvents([...targetEvents, emptyEvent()])}>
                <Plus className="mr-1 h-3 w-3" /> Add event
              </Button>
            </div>
            {targetEvents.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Optional — payload templates may use {'{{outputs}}'} to reference action results.
              </p>
            )}
            {targetEvents.map((e, i) => (
              <div key={i} className="space-y-2 rounded-md border p-2">
                <div className="flex gap-2">
                  <select
                    className="flex h-9 flex-1 rounded-md border border-input bg-background px-2 text-sm"
                    value={e.service}
                    onChange={(ev) => setTargetEvents(targetEvents.map((x, idx) => (idx === i ? { ...x, service: ev.target.value } : x)))}
                  >
                    {serviceList.length > 0 ? serviceList.map((s) => <option key={s} value={s}>{s}</option>) : <option value="communication">communication</option>}
                  </select>
                  <select
                    className="flex h-9 w-32 rounded-md border border-input bg-background px-2 text-sm"
                    value={e.method}
                    onChange={(ev) => setTargetEvents(targetEvents.map((x, idx) => (idx === i ? { ...x, method: ev.target.value } : x)))}
                  >
                    <option value="post">post</option>
                    <option value="emit">emit</option>
                    <option value="publish">publish</option>
                  </select>
                  <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => setTargetEvents(targetEvents.filter((_, idx) => idx !== i))}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
                <JsonField
                  label="Template"
                  value={e.payloadTemplate}
                  onChange={(v) => setTargetEvents(targetEvents.map((x, idx) => (idx === i ? { ...x, payloadTemplate: v } : x)))}
                  height={80}
                  hint={'Use `{{outputs}}` to interpolate action results (BULK-2).'}
                />
              </div>
            ))}
            {form.listenerEnabled && (
              <p className="text-xs text-muted-foreground">
                Listen mode will be configured after creation via the listen endpoint (BULK-4).
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => props.onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={isLoading}>{isLoading ? 'Creating…' : 'Create (draft)'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Detail dialog with live child states ─────────────────────────────────────

function BulkDetailDialog({ op, onClose }: { op: BulkOperation; onClose: () => void }) {
  const toast = useToast();
  const { data: statusDetail } = useGetBulkOperationStatusQuery(op._id);
  const [cancel] = useCancelBulkOperationMutation();
  const [listen] = useListenBulkOperationMutation();

  const childStates =
    (statusDetail as { childStates?: Array<{ jobId?: string; name?: string; status: string; returnvalue?: unknown; error?: string }> } | undefined)?.childStates ??
    [];
  const outputs = (statusDetail as { outputs?: unknown[] } | undefined)?.outputs ?? op.outputs ?? [];

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {op.name}
            <Badge variant={STATUS_VARIANT[op.status] ?? 'secondary'} className="text-[10px]">{op.status}</Badge>
          </DialogTitle>
          <DialogDescription>
            {op.description ?? 'Bulk operation'} · created {format(new Date(op.createdAt), 'MMM d, yyyy HH:mm')}
            {op.approvalId && (
              <Link to="/approvals" className="ml-2 text-primary hover:underline">approval {op.approvalId.slice(0, 8)}…</Link>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap gap-1.5">
            {op.actions.map((a, i) => (
              <Badge key={i} variant="outline" className="font-mono text-[10px]">{a.service}:{a.method}</Badge>
            ))}
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Child states (live)</Label>
            {childStates.length === 0 ? (
              <p className="text-xs text-muted-foreground">No child states yet.</p>
            ) : (
              <div className="space-y-1.5">
                {childStates.map((c, i) => (
                  <div key={i} className="flex items-center justify-between rounded-md border px-3 py-1.5 text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      {c.status === 'running' || c.status === 'active' ? <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" /> : <ListChecks className="h-3.5 w-3.5 text-muted-foreground" />}
                      <span className="truncate font-mono text-xs">{c.name ?? c.jobId ?? `child ${i}`}</span>
                    </div>
                    <Badge variant="outline" className="text-[10px]">{c.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>

          {outputs.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs">Outputs ({outputs.length})</Label>
              <div className="rounded-md border p-2">
                <JsonViewer data={outputs.length === 1 ? outputs[0] : outputs} />
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                listen({ id: op._id, eventFilter: [{ channel: 'bulk.events', eventTypes: ['*'] }] })
                  .unwrap()
                  .then(() => toast.success('Listen mode registered'))
                  .catch((err) => toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Listen failed'))
              }
            >
              Register listener (BULK-4)
            </Button>
            {(op.status === 'running' || op.status === 'pending-approval') && (
              <Button
                variant="outline"
                size="sm"
                className="text-destructive"
                onClick={() =>
                  cancel(op._id)
                    .unwrap()
                    .then(() => toast.success('Cancelled'))
                    .catch((err) => toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Cancel failed'))
                }
              >
                Cancel
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}